import { useState, useEffect, useCallback } from 'react';

export type TourStep = {
  title: string;
  description: string;
  icon: string;
  highlightSelector?: string;
};

type Props = {
  storageKey: string;
  steps: TourStep[];
  title: string;
};

export default function ContextualTour({ storageKey, steps, title }: Props) {
  const [visible, setVisible] = useState(false);
  const [step, setStep] = useState(0);
  const [dontShowAgain, setDontShowAgain] = useState(false);
  const [minimized, setMinimized] = useState(false);

  useEffect(() => {
    const done = localStorage.getItem(storageKey);
    if (!done) {
      const timer = setTimeout(() => setVisible(true), 800);
      return () => clearTimeout(timer);
    }
  }, [storageKey]);

  const finish = useCallback(() => {
    if (dontShowAgain) {
      localStorage.setItem(storageKey, '1');
    }
    setVisible(false);
  }, [dontShowAgain, storageKey]);

  const next = useCallback(() => {
    if (step < steps.length - 1) {
      setStep(step + 1);
      setMinimized(false);
    } else {
      finish();
    }
  }, [step, steps.length, finish]);

  if (!visible) return null;

  const current = steps[step];
  const isLast = step === steps.length - 1;

  if (minimized) {
    return (
      <div className="fixed bottom-6 right-6 z-[90]">
        <button
          onClick={() => setMinimized(false)}
          className="flex items-center gap-2 px-4 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-semibold shadow-lg hover:bg-primary-600 transition-all whitespace-nowrap animate-in"
        >
          <i className={current.icon}></i>
          {title}
          <span className="text-xs opacity-70">
            {step + 1}/{steps.length}
          </span>
        </button>
      </div>
    );
  }

  return (
    <div className="fixed bottom-6 right-6 z-[90] max-w-[360px] w-full animate-in">
      <div className="bg-background-50 rounded-xl border border-background-200/70 shadow-xl overflow-hidden">
        {/* Header */}
        <div className="px-5 py-3 border-b border-background-200/70 flex items-center justify-between bg-background-100/30">
          <div className="flex items-center gap-2 min-w-0">
            <div className="w-7 h-7 rounded-lg bg-primary-50 flex items-center justify-center shrink-0">
              <i className={`${current.icon} text-primary-600 text-sm`}></i>
            </div>
            <span className="text-xs font-semibold text-foreground-700 truncate">{title}</span>
          </div>
          <div className="flex items-center gap-1 ml-2 shrink-0">
            <span className="text-[10px] text-foreground-400">
              {step + 1}/{steps.length}
            </span>
            <button
              onClick={() => setMinimized(true)}
              className="w-6 h-6 rounded flex items-center justify-center text-foreground-400 hover:text-foreground-600 hover:bg-background-100 transition-colors"
            >
              <i className="ri-subtract-line text-xs"></i>
            </button>
            <button
              onClick={finish}
              className="w-6 h-6 rounded flex items-center justify-center text-foreground-400 hover:text-foreground-600 hover:bg-background-100 transition-colors"
            >
              <i className="ri-close-line text-xs"></i>
            </button>
          </div>
        </div>

        {/* Progress bar */}
        <div className="h-1 bg-background-200/60">
          <div
            className="h-full bg-primary-500 transition-all duration-300 rounded-r"
            style={{ width: `${((step + 1) / steps.length) * 100}%` }}
          />
        </div>

        {/* Body */}
        <div className="p-5">
          <h4 className="text-sm font-bold text-foreground-900 mb-1.5">{current.title}</h4>
          <p className="text-sm text-foreground-600 leading-relaxed">{current.description}</p>

          {/* Don't show again */}
          <div className="mt-4 flex items-center gap-2">
            <label className="inline-flex items-center gap-2 cursor-pointer select-none">
              <div className="relative">
                <input
                  type="checkbox"
                  checked={dontShowAgain}
                  onChange={(e) => setDontShowAgain(e.target.checked)}
                  className="sr-only"
                />
                <div
                  className={`w-3.5 h-3.5 rounded border flex items-center justify-center transition-colors ${dontShowAgain ? 'bg-primary-500 border-primary-500' : 'border-foreground-300 bg-transparent'}`}
                >
                  {dontShowAgain && (
                    <i className="ri-check-line text-[8px] text-background-50 font-bold"></i>
                  )}
                </div>
              </div>
              <span className="text-[11px] text-foreground-400">Não mostrar novamente</span>
            </label>
          </div>
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t border-background-200/70 flex items-center justify-between bg-background-100/20">
          <button
            onClick={() => setStep(Math.max(0, step - 1))}
            disabled={step === 0}
            className={`text-xs font-medium transition-colors whitespace-nowrap ${step === 0 ? 'text-foreground-300 cursor-not-allowed' : 'text-foreground-500 hover:text-foreground-700'}`}
          >
            <i className="ri-arrow-left-s-line mr-0.5"></i>Voltar
          </button>

          <div className="flex items-center gap-2">
            <button
              onClick={finish}
              className="text-xs text-foreground-400 hover:text-foreground-600 font-medium transition-colors whitespace-nowrap"
            >
              Pular
            </button>
            <button
              onClick={next}
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md bg-primary-500 text-background-50 text-xs font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap"
            >
              {isLast ? 'Concluir' : 'Próximo'}
              <i className={`${isLast ? 'ri-check-line' : 'ri-arrow-right-s-line'} text-xs`}></i>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
